import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, LogOut, LayoutDashboard, LockKeyhole, Sun, Moon, FolderTree, Upload } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export function Header() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const isAdmin = user && typeof user === "object" && user.role === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-3 sm:top-5 z-40 px-3 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="glass-header rounded-full border border-white/60 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)] px-4 sm:px-5 py-2.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group min-w-0" data-testid="header-home-link">
            <img
              src="/brand/pdfplays-icon.png"
              alt="PDFPLAYS logo"
              className="h-10 w-10 sm:h-11 sm:w-11 shrink-0 rounded-xl object-cover transition-transform group-hover:scale-105"
              loading="eager"
              decoding="async"
            />
            <div className="leading-tight min-w-0">
              <div className="font-display text-base sm:text-lg font-black tracking-tight text-slate-900 truncate">PDFPLAYS</div>
            </div>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-10 w-10 p-0 rounded-full text-slate-700 hover:text-slate-900 hover:bg-slate-100"
                aria-label="Open menu"
                data-testid="header-menu-btn"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={10}
              className="w-56 rounded-xl"
              data-testid="header-menu"
            >
              <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                Appearance
              </DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  toggle();
                }}
                data-testid="header-menu-theme"
                className="gap-2 cursor-pointer"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                <span>{theme === "dark" ? "Light mode" : "Dark mode"}</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                {isAdmin ? "Admin" : "Account"}
              </DropdownMenuLabel>

              {isAdmin ? (
                <>
                  <DropdownMenuItem
                    onSelect={() => navigate("/admin")}
                    data-testid="header-menu-admin"
                    className="gap-2 cursor-pointer"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <span>Manage PDFs</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => navigate("/admin/categories")}
                    data-testid="header-menu-categories"
                    className="gap-2 cursor-pointer"
                  >
                    <FolderTree className="h-4 w-4" />
                    <span>Categories</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => navigate("/admin/import")}
                    data-testid="header-menu-import"
                    className="gap-2 cursor-pointer"
                  >
                    <Upload className="h-4 w-4" />
                    <span>Bulk Import</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={handleLogout}
                    data-testid="header-menu-logout"
                    className="gap-2 cursor-pointer text-rose-600 focus:text-rose-700"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Sign out</span>
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem
                  onSelect={() => navigate("/admin/login")}
                  data-testid="header-menu-login"
                  className="gap-2 cursor-pointer"
                >
                  <LockKeyhole className="h-4 w-4" />
                  <span>Admin sign in</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
